import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LoopIcon from "@mui/icons-material/Loop";
import CollectionsIcon from "@mui/icons-material/Collections";
import Header from "../Mobile/Dashboard/Header/Header";
import Footer from "../Mobile/Dashboard/Footer/Footer";
import "./Desktop.css";
import Gallery from "../Mobile/Visualiser/Gallery/Gallery";
import ContactForm from "../Shared/ContactForm/ContactForm";
import DraggableHandle from "./DraggableHandle";

import { useDoorLogic } from "../../Hooks/useDoorLogic";
import { useImageHandling } from "../../Hooks/useImageHandling";
import { useDragging } from "../../Hooks/useDragging";
import { useAnalytics } from "../../Hooks/useAnalytics";

import DoorOptions from "../DoorOptions";
import ColorSelector from "../ColorSelector";
import WindowOptions from "../WindowOptions";

import {
  INITIAL_DOOR_DIMENSIONS,
  CANVAS_DIMENSIONS,
} from "../../Constants/constants";

function Customise() {
  // Modal and UI state
  const [anchorEl, setAnchorEl] = useState(null);
  const [view, setView] = useState("detail");
  const [selectedRect, setSelectedRect] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  // Custom hooks
  const doorLogic = useDoorLogic();
  const { trackVisualiserSave, trackVisualiserQuote } = useAnalytics();
  // console.log(doorLogic)
  const imageHandling = useImageHandling(
    doorLogic.points,
    doorLogic.image,
    doorLogic.backImage,
    doorLogic.setBackImage,
    doorLogic.setPoints
  );
  const dragging = useDragging(
    doorLogic.points,
    doorLogic.setPoints,
    doorLogic.image
  );

  // Handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleClosed = () => setOpen(false);
  const handleClose = () => setAnchorEl(null);
  const openModal = () => {
    setOpen(true);
    handleClose();
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const handleChange = (e) => {
    doorLogic.setDoorWidth(e.target.value);
  };

  useEffect(() => {
    if (isModalOpen && formRef.current) {
      formRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [isModalOpen]);

  return (
    <>
    <Header/>
      <section className="customise mt-3">
        <div className="container">
          <div className="row">
            {/* Left Panel - Controls */}
            <div
              className="col-md-4 pt-2 pb-4 sticky-top"
              style={{ backgroundColor: "#f8f8f8", borderRadius: "3px" }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <h4>Design Centre</h4>
                <div className="d-flex align-items-center">
                  <button
                    className="btn save-btn d-flex align-items-center"
                    onClick={() => {
                      trackVisualiserSave({
                        door_style: doorLogic.pattern || 'Not specified',
                        door_series: doorLogic.series || 'Not specified',
                        door_design: doorLogic.design || 'Not specified',
                        door_colour: doorLogic.color || 'Not specified',
                        panel_style: doorLogic.style || 'Not specified',
                        source: 'desktop_customise'
                      });
                      imageHandling.handleSaveImage();
                    }}
                  >
                    <SaveAltIcon className="me-2" />
                    Save
                  </button>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center menu-btn mt-2">
                <Link to="/visualiser">
                  <button className="btn" onClick={doorLogic.resetToDefault}>
                    <LoopIcon /> Start Again
                  </button>
                </Link>
                <button
                  className="btn"
                  onClick={() => fileInputRef.current.click()}
                >
                  <CloudUploadIcon /> Upload
                </button>
                <button className="btn" onClick={openModal}>
                  <CollectionsIcon /> Gallery
                </button>
              </div>

              <hr />

              <Box>
                <ToggleButtonGroup
                  value={view}
                  exclusive
                  onChange={handleViewChange}
                  aria-label="View Toggle"
                  sx={{ mb: 2 }}
                  className="info-btn"
                >
                  <ToggleButton value="detail" className="info-btn">
                    Details
                  </ToggleButton>
                  <ToggleButton value="style" className="info-btn">
                    Style & Color
                  </ToggleButton>
                  <ToggleButton value="customisation" className="info-btn">
                    Window Options
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Details View */}
                {view === "detail" && (
                  <div className="details-box">
                    <div>
                      <p className="mb-1">Your Door Width</p>
                      <select
                        value={doorLogic.doorWidth}
                        onChange={handleChange}
                      >
                        <option value="double door">Double Door</option>
                        <option value="single door">Single Door</option>
                      </select>
                    </div>
                    {doorLogic.selectedPattern === "sectional" && (
                      <div className="">
                        <p className="mb-1">Series</p>
                        <p className="dynamic-p">{doorLogic.selectSeries}</p>
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="mb-1">Door Design</p>
                      <p className="dynamic-p">
                        {doorLogic.selectedDesign || "Roller"}
                      </p>
                    </div>
                    <div className="mt-3">
                      <p className="mb-1">Door Colour</p>
                      <p className="dynamic-p">{doorLogic.colorname}</p>
                    </div>
                  </div>
                )}

                {/* Style & Color View */}
                {view === "style" && (
                  <>
                    <DoorOptions
                      selectedPattern={doorLogic.selectedPattern}
                      selectSeries={doorLogic.selectSeries}
                      selectedDesign={doorLogic.selectedDesign}
                      selectedPanelStyle={doorLogic.selectedPanelStyle}
                      SelectedMaterial={doorLogic.SelectedMaterial}
                      handlePatternChange={doorLogic.handlePatternChange}
                      handleSeriesChange={doorLogic.handleSeriesChange}
                      setSelectedDesign={doorLogic.setSelectedDesign}
                      handlePanelStyleChange={doorLogic.handlePanelStyleChange}
                      setSelectedMaterial={doorLogic.setSelectedMaterial}
                      applyDoorDesign={doorLogic.applyDoorDesign}
                      selectedColor={doorLogic.selectedColor}
                    />

                    <ColorSelector
                      selectSeries={doorLogic.selectSeries}
                      selectedDesign={doorLogic.selectedDesign}
                      selectedPanelStyle={doorLogic.selectedPanelStyle}
                      selectedColor={doorLogic.selectedColor}
                      setSelectedColor={doorLogic.setSelectedColor}
                      setColorName={doorLogic.setColorName}
                      selectedRect={selectedRect}
                      applyDoorDesign={doorLogic.applyDoorDesign}
                      selectedPattern={doorLogic.selectedPattern}
                    />
                  </>
                )}

                {/* Window Options View */}
                {view === "customisation" && (
                  <WindowOptions
                    selectedDesign={doorLogic.selectedDesign}
                    selctwindow={doorLogic.selctwindow}
                    setSelectWindow={doorLogic.setSelectWindow}
                  />
                )}
              </Box>

              <button className="btn mt-3 free-qoute" onClick={() => {
                trackVisualiserQuote({
                  door_style: doorLogic.pattern || 'Not specified',
                  door_series: doorLogic.series || 'Not specified',
                  door_design: doorLogic.design || 'Not specified',
                  door_colour: doorLogic.color || 'Not specified',
                  panel_style: doorLogic.style || 'Not specified',
                  door_width: doorLogic.width || 'Not specified',
                  source: 'desktop_customise'
                });
                handleOpenModal();
              }}>
                CONTACT US FOR FREE QUOTE
              </button>

              {/* Contact Form Modal */}

              {isModalOpen && (
                <div className="contact-modal-overlay">
                  <div className="contact-modal-content" ref={formRef}>
                    <button
                      className="contact-modal-close"
                      onClick={handleCloseModal}
                      aria-label="Close contact form"
                    >
                      ✕
                    </button>
                    <ContactForm
                      onClose={handleCloseModal}
                      series={doorLogic.selectSeries}
                      design={doorLogic.selectedDesign}
                      color={doorLogic.colorname}
                      style={doorLogic.PanelStyle}
                      pattern={doorLogic.selectedPattern}
                      doorwidth={doorLogic.doorWidth}
                      // Pass additional props for high-res image generation
                      points={doorLogic.points}
                      doorImage={doorLogic.image}
                      bgImage={doorLogic.bgImage}
                      houseImageDimensions={imageHandling.houseImageDimensions}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel - Image Display */}
            <div className="col-md-8 file">
              <div className="sticky-div">
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={imageHandling.handleImageUpload}
                />

                {!imageHandling.bgKonvaImage && (
                  <div
                    className="text-center mt-5 upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: "40px" }} />
                    <p>Upload image</p>
                  </div>
                )}

                <div
                  ref={dragging.containerRef}
                  className="overflow-hidden position-relative"
                  id="image-display-area"
                  style={{
                    width: `${CANVAS_DIMENSIONS.WIDTH}px`,
                    height: `${CANVAS_DIMENSIONS.HEIGHT}px`,
                  }}
                >
                  {/* Background Image */}
                  {imageHandling.bgKonvaImage && (
                    <img
                      src={imageHandling.bgKonvaImage}
                      alt="Background"
                      onLoad={(e) => {
                        const target = e.target;
                        imageHandling.setHouseImageDimensions({
                          width: target.naturalWidth,
                          height: target.naturalHeight,
                        });
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                      }}
                    />
                  )}

                  {/* Door Overlay */}
                  {doorLogic.image && (
                    <div
                      className="door-wrapper position-absolute"
                      style={{
                        width: `${INITIAL_DOOR_DIMENSIONS.WIDTH}px`,
                        height: `${INITIAL_DOOR_DIMENSIONS.HEIGHT}px`,
                        transformOrigin: "0 0",
                        transform: dragging.imageTransform,
                        backfaceVisibility: "hidden",
                        willChange: "transform",
                        cursor: dragging.isDraggingDoor ? "grabbing" : "grab",
                        top: 0,
                        touchAction: "none",
                      }}
                      onMouseDown={dragging.handleMouseDownOnDoor}
                      onTouchStart={dragging.handleMouseDownOnDoor}
                    >
                      <img
                        src={doorLogic.image}
                        alt="Selected door"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          pointerEvents: "none",
                          zIndex: 10,
                        }}
                      />
                    </div>
                  )}

                  {/* Draggable Handles */}
                  {dragging.showHandles &&
                    doorLogic.image &&
                    doorLogic.points.map((point, index) => (
                      <DraggableHandle
                        key={index}
                        id={index}
                        x={point.x}
                        y={point.y}
                        onMouseDown={dragging.handleMouseDownOnHandle}
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer/>

      {/* Gallery Modal */}
      <Gallery
        open={open}
        handleClose={handleClosed}
        selectgalleryimage={imageHandling.selectgalleryimage}
      />
    </>
  );
}

export default Customise;
